import React, { useEffect, useState } from "react";
import {
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  SelectChangeEvent,
  Chip,
  Stack,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import {
  fetchAllAdminOrders,
  updateAdminOrderStatus,
} from "../../../State/admin/adminSlice";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const orderStatuses = [
  { color: "#FFA500", label: "PENDING" },
  { color: "#F5BCBA", label: "CONFIRMED" },
  { color: "#F5BCBA", label: "PLACED" },
  { color: "#1E90FF", label: "SHIPPED" },
  { color: "#32CD32", label: "DELIVERED" },
  { color: "#FF0000", label: "CANCELLED" },
] as const;

export default function AdminOrdersTable() {
  const dispatch = useAppDispatch();
  const { orders } = useAppSelector((state) => state.admin);
  const [dateFilter, setDateFilter] = useState<string>("ALL");
  const [anchorElStatus, setAnchorElStatus] = useState<Record<number, HTMLElement | null>>({});

  useEffect(() => {
    dispatch(fetchAllAdminOrders(localStorage.getItem("jwt") || ""));
  }, [dispatch]);

  const handleStatusClick = (event: React.MouseEvent<HTMLButtonElement>, orderId: number) => {
    setAnchorElStatus((prev) => ({ ...prev, [orderId]: event.currentTarget }));
  };

  const handleStatusClose = (orderId: number) => () => {
    setAnchorElStatus((prev) => ({ ...prev, [orderId]: null }));
  };

  const handleUpdateOrderStatus = (orderId: number, statusLabel: string) => () => {
    dispatch(
      updateAdminOrderStatus({
        jwt: localStorage.getItem("jwt") || "",
        orderId,
        orderStatus: statusLabel,
      })
    );
    handleStatusClose(orderId)();
  };

  // Handler to download individual order receipt as PDF via print window layout
  const handleDownloadPdf = (order: any) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const rawPaymentMethod = order.paymentDetails?.paymentMethod || order.paymentMethod || "N/A";
    const paymentStatus = order.paymentDetails?.paymentStatus || order.paymentStatus || "PENDING";
    const rawDate = order.orderDate || order.createdAt || order.creationDate || new Date().toISOString();

    const itemsHtml = (order.orderItems || [])
      .map(
        (item: any) => `
        <tr>
          <td style="padding: 8px; border-bottom: 1px solid #ddd;">${item.product?.title || "Product"}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: center;">${item.quantity || 1}</td>
          <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">KES ${item.sellingPrice || item.price || 0}</td>
        </tr>
      `
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Order Receipt #${order.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 20px; }
            .details { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background-color: #333; color: white; padding: 10px; text-align: left; }
            .total { margin-top: 20px; text-align: right; font-size: 16px; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Order Receipt</h2>
            <p>Order ID: #${order.id}</p>
            <p>Date: ${new Date(rawDate).toLocaleString()}</p>
          </div>
          <div class="details">
            <p><strong>Payment Method:</strong> ${rawPaymentMethod.replace(/_/g, " ")}</p>
            <p><strong>Payment Status:</strong> ${paymentStatus}</p>
            <p><strong>Order Status:</strong> ${order.orderStatus || order.status || "N/A"}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th style="text-align: center;">Quantity</th>
                <th style="text-align: right;">Price</th>
              </tr>
            </thead>
            <tbody>
              ${itemsHtml}
            </tbody>
          </table>
          <div class="total">
            Total Amount: KES ${order.totalSellingPrice || order.totalPrice || 0}
          </div>
          <script>
            window.onload = function() { window.print(); window.close(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Handler to export filtered orders as CSV
  const handleExportCsv = () => {
    const csvRows = [];
    // CSV Header
    csvRows.push(["Order ID", "Order Date", "Seller", "Payment Method", "Payment Status", "Order Status", "Total Amount"].join(","));

    filteredOrders.forEach((order: any) => {
      const rawPaymentMethod = (order.paymentDetails?.paymentMethod || order.paymentMethod || "N/A").replace(/_/g, " ");
      const paymentStatus = order.paymentDetails?.paymentStatus || order.paymentStatus || "PENDING";
      const orderStatus = order.orderStatus || order.status || "N/A";
      const rawDate = order.orderDate || order.createdAt || order.creationDate || "";
      const totalAmount = order.totalSellingPrice || order.totalPrice || 0;
      
      const firstProductSeller = order.orderItems?.[0]?.product?.seller;
      const sellerName = (
        order.seller?.sellerName || 
        order.seller?.businessDetails?.businessName || 
        firstProductSeller?.sellerName || 
        firstProductSeller?.businessDetails?.businessName || 
        `Seller ID: ${order.sellerId || "N/A"}`
      ).replace(/,/g, ""); // Remove commas to keep CSV formatting safe

      csvRows.push(
        [
          `#${order.id}`,
          `"${new Date(rawDate).toLocaleString()}"`,
          `"${sellerName}"`,
          `"${rawPaymentMethod}"`,
          paymentStatus,
          orderStatus,
          totalAmount,
        ].join(",")
      );
    });

    const csvBlob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(csvBlob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `admin_orders_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredOrders = (orders || []).filter((order: any) => {
    if (dateFilter === "24HRS") {
      const rawDate = order.orderDate || order.createdAt || order.creationDate;
      if (!rawDate) return false;
      const orderTime = new Date(rawDate).getTime();
      return Date.now() - orderTime <= 24 * 60 * 60 * 1000;
    }
    return true;
  });

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
        <h1 className="font-bold text-xl">All Seller Orders</h1>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportCsv}
            size="small"
          >
            Export CSV
          </Button>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="date-filter-label">Date Filter</InputLabel>
            <Select
              labelId="date-filter-label"
              value={dateFilter}
              label="Date Filter"
              onChange={(e: SelectChangeEvent) => setDateFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Orders</MenuItem>
              <MenuItem value="24HRS">Last 24 Hours</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 750 }} aria-label="admin order table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Order ID</StyledTableCell>
              <StyledTableCell>Seller Details</StyledTableCell>
              <StyledTableCell>Products</StyledTableCell>
              <StyledTableCell align="center">Payment Details</StyledTableCell>
              <StyledTableCell align="right">Order Status</StyledTableCell>
              <StyledTableCell align="center">Action</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((item: any) => {
              const rawPaymentMethod = item.paymentDetails?.paymentMethod || item.paymentMethod;
              const formattedPaymentMethod = rawPaymentMethod ? rawPaymentMethod.replace(/_/g, " ") : "N/A";
              const paymentStatus = item.paymentDetails?.paymentStatus || item.paymentStatus || "PENDING";
              const rawDate = item.orderDate || item.createdAt || item.creationDate;
              const currentStatus = item.orderStatus || item.status || "PENDING";

              // Extract seller info safely from nested orderItems since order only stores sellerId
              const firstProductSeller = item.orderItems?.[0]?.product?.seller;
              const sellerName = 
                item.seller?.sellerName || 
                item.seller?.businessDetails?.businessName || 
                firstProductSeller?.sellerName || 
                firstProductSeller?.businessDetails?.businessName || 
                `Seller ID: ${item.sellerId || "N/A"}`;
              const sellerEmail = item.seller?.email || firstProductSeller?.email || "";

              return (
                <StyledTableRow key={item.id}>
                  <StyledTableCell>
                    #{item.id}
                    {rawDate && (
                      <div className="text-xs text-gray-500">
                        {new Date(rawDate).toLocaleDateString()}
                      </div>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <div className="font-medium">{sellerName}</div>
                    {sellerEmail && <div className="text-xs text-gray-500">{sellerEmail}</div>}
                  </StyledTableCell>
                  <StyledTableCell>
                    {item.orderItems?.map((orderItem: any, index: number) => (
                      <div key={index} className="text-sm">
                        {orderItem.product?.title} (x{orderItem.quantity})
                      </div>
                    ))}
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <div>{formattedPaymentMethod}</div>
                    <Chip
                      label={paymentStatus}
                      size="small"
                      color={paymentStatus === "COMPLETED" || paymentStatus === "SUCCESS" ? "success" : "warning"}
                      sx={{ mt: 0.5 }}
                    />
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Button
                      onClick={(e) => handleStatusClick(e, item.id)}
                      variant="outlined"
                      size="small"
                    >
                      {currentStatus}
                    </Button>
                    <Menu
                      anchorEl={anchorElStatus[item.id]}
                      open={Boolean(anchorElStatus[item.id])}
                      onClose={handleStatusClose(item.id)}
                    >
                      {orderStatuses.map((status) => (
                        <MenuItem
                          key={status.label}
                          onClick={handleUpdateOrderStatus(item.id, status.label)}
                        >
                          {status.label}
                        </MenuItem>
                      ))}
                    </Menu>
                  </StyledTableCell>
                  <StyledTableCell align="center">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadPdf(item)}
                    >
                      Receipt
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}